import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { PlaidLink } from "react-plaid-link";
import { toast } from "react-toastify";

import ai from "../../helpers/axios";
import Loader from "../loader";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import PlaidLinkedAccountsTable from "./plaid-linked-accounts-table";
import XeroTokensTable from "./xero-tokens-table";
import StripeAccountsTable from "./stripe-accounts-table";

const PaymentsLinkingContent = () => {
  const history = useHistory();
  const { user, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const [isPlaidLoading, setIsPlaidLoading] = useState(true);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [isXeroLoading, setIsXeroLoading] = useState(true);
  const [xeroLink, setXeroLink] = useState('');
  const [xeroTokens, setXeroTokens] = useState([]);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeConnections, setStripeConnections] = useState([]);

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdmin = userRoles.some((role) => "Super Admin" === role);

  useEffect(() => {
    if (!superAdmin) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [superAdmin, history]);

  useEffect(() => {
    document.title = "Payments Linking | evoJets HQ";
  }, []);

  const generatePlaidToken = useCallback(async () => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });
    let data = await ai.auth(token).post(`/api/plaid/create-link-token`);
    if (data.data.link_token) {
      setPlaidLinkToken(data.data.link_token);
      setIsPlaidLoading(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const generateXeroUrl = useCallback(async () => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });
    let data = await ai.auth(token).post(`/api/xero/build-consent-url`);
    if (data.data.consentUrl) {
      setXeroLink(data.data.consentUrl);
      setIsXeroLoading(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getPlaidAccountsData = useCallback(async () => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).get("/api/plaid/access-tokens");

      setPlaidAccounts(response.data.data);
    } catch (error) {}
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getXeroTokensData = useCallback(async () => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).get("/api/xero/access-tokens");

      setXeroTokens(response.data.data);
    } catch (error) {}
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getStripeConnectionsData = useCallback(async () => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).get("/api/stripe/connections");

      setStripeConnections(response.data.data);
    } catch (error) {
    } finally {
      setIsStripeLoading(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    generatePlaidToken();
    generateXeroUrl();
    getPlaidAccountsData();
    getXeroTokensData();
    getStripeConnectionsData();
  }, [generatePlaidToken, generateXeroUrl, getPlaidAccountsData, getXeroTokensData, getStripeConnectionsData]);

  const handleConnectPlaid = useCallback(async (plaidPublicToken) => {
    setIsPlaidLoading(true);

    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      await ai.auth(token).post("/api/plaid/access-tokens", {
        publicToken: plaidPublicToken,
      });

      await getPlaidAccountsData();

      toast.success("Your plaid account has been connected successfully.");
    } catch (error) {
      toast.error("There was an error connecting your plaid account.");
    } finally {
      setIsPlaidLoading(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect, getPlaidAccountsData]);

  const handleUnlinkPlaidAccount = useCallback(async (accessTokenId) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/plaid/access-tokens/${accessTokenId}/unlink`);

      if (response.data) {
        const { _id } = response.data.data;

        setPlaidAccounts(
          accounts => accounts.map(
            account => account._id === _id ?
              {
                ...account,
                disabled: true,
                accounts: account.accounts.map(acc => ({ ...acc, disabled: true })),
              } : account,
          ),
        );
      }

      toast.success("Plaid account was unlinked successfully");
    } catch (error) {
      toast.error("Failed to unlink plaid account");
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleModifyPlaidAccount = useCallback(async (accountId, data) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/plaid/accounts/${accountId}`, data);

      if (response.data) {
        const { _id, accessTokenId, ...rest } = response.data.data;

        setPlaidAccounts(
          accounts => accounts.map(
            account => account._id === accessTokenId ?
              {
                ...account,
                accounts: account.accounts.map(acc => acc._id === _id ? { ...acc, ...rest } : acc),
              } : account,
          ),
        );
      }

      toast.success("Plaid account was updated successfully");
    } catch (error) {
      toast.error("Failed to update plaid account");
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleModifyXeroAccount = useCallback(async (accountId, data) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/xero/accounts/${accountId}`, data);

      if (response.data) {
        getXeroTokensData();
      }

      toast.success("Xero account was updated successfully");
    } catch (error) {
      toast.error("Failed to update account");
    }
  }, [getAccessTokenSilently, loginWithRedirect, getXeroTokensData]);

  const handleModifyXeroTenant = useCallback(async (tenantId, data) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/xero/tenants/${tenantId}`, data);

      if (response.data) {
        getXeroTokensData();
      }

      toast.success("Xero tenant was updated successfully");
    } catch (error) {
      toast.error("Failed to update tenant");
    }
  }, [getAccessTokenSilently, loginWithRedirect, getXeroTokensData]);

  const handleSyncStripe = useCallback(async () => {
    setIsStripeLoading(true);

    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      await ai.auth(token).post("/api/stripe/sync", {});

      await getStripeConnectionsData();

      toast.success("Your stripe account has been connected successfully.");
    } catch (error) {
      toast.error("There was an error connecting your stripe account.");
    } finally {
      setIsStripeLoading(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect, getStripeConnectionsData]);

  const handleModifyStripeAccount = useCallback(async (accountId, data) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/stripe/accounts/${accountId}`, data);

      if (response.data) {
        const { _id, connectionId, ...rest } = response.data.data;

        setStripeConnections(
          connections => connections.map(
            connection => connection._id === connectionId ?
              {
                ...connection,
                accounts: connection.accounts.map(acc => acc._id === _id ? { ...acc, ...rest } : acc),
              } : connection,
          ),
        );
      }

      toast.success("Stripe account was updated successfully");
    } catch (error) {
      toast.error("Failed to update Stripe account");
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleDisconnectStripe = useCallback(async (connectionId) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    try {
      const response = await ai.auth(token).patch(`/api/stripe/connections/${connectionId}`, { disabled: true });

      if (response.data) {
        const { _id } = response.data.data;

        setStripeConnections(
          connections => connections.map(
            connection => connection._id === _id ?
              {
                ...connection,
                disabled: true,
                accounts: connection.accounts.map(acc => ({ ...acc, disabled: true })),
              } : connection,
          ),
        );
      }

      toast.success("Stripe account was disconnected successfully");
    } catch (error) {
      toast.error("Failed to disconnect Stripe account");
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={
          <div className="d-flex align-items-center mb-1">
            <h5 className="mb-0">Plaid Accounts</h5>

            {plaidLinkToken && (
              <span data-testid="plaid-connect-button">
                <PlaidLink
                  className="base-button icon-button plaid-link-button ml-2"
                  token={plaidLinkToken}
                  onSuccess={handleConnectPlaid}
                  title="Connect a plaid bank account"
                >
                  <span className="material-icons">add_link</span>
                </PlaidLink>
              </span>
            )}
          </div>
        }
        extraClasses="page-header-trips-l1"
      />

      <div className="position-relative">
        {isPlaidLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle" />}

        <PlaidLinkedAccountsTable
          data={plaidAccounts}
          handleUnlinkAccount={handleUnlinkPlaidAccount}
          handleModifyAccount={handleModifyPlaidAccount}
        />
      </div>

      <PageHeaderRowSearch
        h1={
          <div className="d-flex align-items-center mb-1">
            <h5 className="mb-0">Xero Accounts</h5>

            {xeroLink && (
              <a
                className="base-button icon-button plaid-link-button ml-2"
                href={xeroLink}
                title="Connect a xero"
                data-testid="xero-connect-button"
              >
                <span className="material-icons">add_link</span>
              </a>
            )}
          </div>
        }
        extraClasses="page-header-trips-l1"
      />

      <div className="position-relative">
        {isXeroLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle" />}

        <XeroTokensTable
          data={xeroTokens}
          handleModifyAccount={handleModifyXeroAccount}
          handleModifyTenant={handleModifyXeroTenant}
        />
      </div>

      <PageHeaderRowSearch
        h1={
          <div className="d-flex align-items-center mb-1">
            <h5 className="mb-0">Stripe Accounts</h5>

            <button
              className="base-button icon-button plaid-link-button ml-2"
              onClick={handleSyncStripe}
              title="Connect a stripe account"
              type="button"
              data-testid="stripe-connect-button"
            >
              <span className="material-icons">add_link</span>
            </button>
          </div>
        }
        extraClasses="page-header-trips-l1"
      />

      <div className="position-relative">
        {isStripeLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle" />}

        <StripeAccountsTable
          data={stripeConnections}
          handleModifyAccount={handleModifyStripeAccount}
          handleDisconnect={handleDisconnectStripe}
        />
      </div>
    </Fragment>
  );
};

export default PaymentsLinkingContent;
